<template>
  <header class="main-header" :class="{ 
      'scrolled': isScrolled, 
      'navbar-hidden': !navbarVisible 
    }">
    <nav class="navbar navbar-expand-lg">
      <div class="container">
        <router-link class="navbar-brand" to="/">
          <img src="../assets/tutormed-logo.png" alt="TutorMed Logo" height="40">
        </router-link>
        
        <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" 
                aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
          <span class="navbar-toggler-icon"></span>
        </button>
        
        <div class="collapse navbar-collapse" id="navbarNav">
          <ul class="navbar-nav mx-auto">
            <li class="nav-item">
              <router-link class="nav-link" to="/">
                <i class="bi bi-house"></i>
                <span>Home</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" to="/about">
                <i class="bi bi-info-circle"></i>
                <span>About</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" to="/programs">
                <i class="bi bi-collection"></i>
                <span>Programs</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" to="/free-resources">
                <i class="bi bi-lightning-charge"></i>
                <span>Free Resources</span>
              </router-link>
            </li>
            <li class="nav-item" v-if="isLoggedIn">
              <router-link class="nav-link" to="/dashboard">
                <i class="bi bi-speedometer2"></i>
                <span>Dashboard</span>
              </router-link>
            </li>
          </ul>
          
          <div class="nav-actions">
            <template v-if="!isLoggedIn">
              <router-link to="/login" class="btn btn-nav-login">
                <i class="bi bi-box-arrow-in-right"></i>
                <span>Login</span>
              </router-link>
              <router-link to="/register" class="btn btn-nav-register">
                <i class="bi bi-person-plus"></i>
                <span>Register</span>
              </router-link>
            </template>
            <template v-else>
              <div class="nav-icons">
                <!-- User Menu -->
                <div class="dropdown user-dropdown">
                  <button class="btn dropdown-toggle" type="button" id="userDropdown" 
                          data-bs-toggle="dropdown" aria-expanded="false">
                    <div class="user-avatar" v-if="!userAvatar">
                      <span>{{ userInitial }}</span>
                    </div>
                    <img v-else :src="userAvatar" alt="Profile" class="user-avatar-img">
                    <span class="user-name d-none d-md-inline">{{ userName }}</span>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end" data-bs-theme="dark" aria-labelledby="userDropdown">
                    <li class="dropdown-user-header">
                      <div class="d-flex align-items-center">
                        <div class="user-avatar-large" v-if="!userAvatar">
                          <span>{{ userInitial }}</span>
                        </div>
                        <img v-else :src="userAvatar" alt="Profile" class="user-avatar-large-img">
                        <div class="user-details">
                          <h6 class="mb-0">{{ userName }}</h6>
                          <small>{{ userCategoryName }}</small>
                        </div>
                      </div>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <router-link class="dropdown-item" to="/dashboard">
                        <i class="bi bi-speedometer2"></i> Dashboard
                      </router-link>
                    </li>
                    <li>
                      <router-link class="dropdown-item" to="/notifications">
                        <i class="bi bi-bell"></i> Notifications
                        <span v-if="notificationCount > 0" class="badge rounded-pill bg-primary ms-2">{{ notificationCount }}</span>
                      </router-link>
                    </li>
                    <li>
                      <router-link class="dropdown-item" to="/messages">
                        <i class="bi bi-chat-left-text"></i> Messages
                      </router-link>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="handleLogout">
                        <i class="bi bi-box-arrow-right"></i> Logout
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
// Import specific Bootstrap components to avoid issues
import { Dropdown, Collapse } from 'bootstrap';
import api from '@/services/api';
import { getSubcategoryDisplayName, getCategoryDisplayName } from '@/utils/categoryConstants';
console.log("Bootstrap imports loaded:", { Dropdown: !!Dropdown, Collapse: !!Collapse });

export default {
  name: 'Navbar',
  data() {
    return {
      notificationCount: 0, // Will be updated from API
      dropdowns: [], // Store dropdown instances
      navbarCollapse: null, // Store navbar collapse instance
      isScrolled: false, // Track scroll state
      lastScrollY: 0, // Track last scroll position
      scrollDirection: 'up', // Changed default direction to up so initial state tends to show navbar
      navbarVisible: true, // Control navbar visibility - always true initially
      notificationInterval: null, // Interval for checking notifications
    }
  },
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV !== 'production';
    },
    isLoggedIn() {
      // Enhanced check for authentication status
      const storeToken = this.$store?.state?.authToken;
      const localToken = localStorage.getItem('authToken');
      const isAuthed = !!storeToken || !!localToken;
      
      // Log authentication status in development mode
      if (process.env.NODE_ENV !== 'production') {
        console.log('Auth Check: ', {
          storeToken: !!storeToken,
          localToken: !!localToken,
          result: isAuthed
        });
      }
      
      return isAuthed;
    },
    userName() {
      // Get user info from Vuex or localStorage
      const user = this.$store?.state?.user || JSON.parse(localStorage.getItem('user') || '{}')
      return user.first_name || user.username || 'User'
    },
    userInitial() {
      const name = this.userName;
      return name ? name.charAt(0).toUpperCase() : 'U';
    },
    userCategoryName() {
      const user = this.$store?.state?.user || JSON.parse(localStorage.getItem('user') || '{}')
      
      // Priority 1: Use subcategory if available for more specific display
      if (user.subcategory) {
        return getSubcategoryDisplayName(user.subcategory);
      }
      
      // Priority 2: Use category_name if available (from backend)
      if (user.category_name) {
        return user.category_name;
      }
      
      // Priority 3: Map category to display name using constants
      if (user.category) {
        return getCategoryDisplayName(user.category);
      }
      
      // Final fallback
      return 'User';
    },
    // Get user avatar if available, otherwise use initials
    userAvatar() {
      const user = this.$store?.state?.user || JSON.parse(localStorage.getItem('user') || '{}')
      
      // If profile_picture_url is provided from backend, use it
      if (user.profile_picture_url) {
        console.log("Using profile picture URL from user data:", user.profile_picture_url)
        
        // Make sure the URL has the correct base if it's a relative path
        if (user.profile_picture_url.startsWith('/media')) {
          return `${window.location.protocol}//${window.location.hostname}:8000${user.profile_picture_url}`;
        }
        return user.profile_picture_url;
      }
      
      // Check category for default icon if we have category info
      if (user.category) {
        console.log("Using category-based avatar for category:", user.category)
        
        // Map category IDs to icon paths - UPDATED to use public directory
        const categoryIcons = {
          1: '/media/defaults/nurse-icon.png',           // Nurse
          2: '/media/defaults/pa-icon.png',              // Physician Assistant
          3: '/media/defaults/student-icon.png',         // Medical Student
          4: '/media/defaults/doctor-icon.png',          // Doctor
          5: '/media/defaults/exam-icon.png'             // Foreign Exam
        };
        
        // If we have a mapping for this category
        if (categoryIcons[user.category]) {
          return categoryIcons[user.category];
        }
      }
      
      // Final fallback - check if user has gender info for default icon
      if (user.sex === 'M') {
        return '/media/defaults/doctor-icon.png';
      } else if (user.sex === 'F') {
        return '/media/defaults/nurse-icon.png';
      }
      
      return null; // No avatar found, will show initials
    }
  },
  mounted() {
    console.log("MainNavbar mounted");
    this.initDropdowns();
    this.initNavbarCollapse();
    
    // Ensure navbar is visible initially
    this.isScrolled = false;
    this.navbarVisible = true;
    
    // Initialize scroll event listener
    window.addEventListener('scroll', this.handleScroll);
    // Initialize immediately with current scroll position
    this.$nextTick(() => {
      this.handleScroll();
    });
    
    // Start polling for notification count if user is logged in
    if (this.isLoggedIn) {
      this.fetchNotificationCount();
      this.notificationInterval = setInterval(() => {
        this.fetchNotificationCount();
      }, 30000); // Check every 30 seconds
    }
  },
  updated() {
    console.log("MainNavbar updated");
    // Re-initialize dropdowns when component updates
    this.initDropdowns();
    this.initNavbarCollapse();
  },
  methods: {
    initDropdowns() {
      console.log("Initializing dropdown menus");
      
      // Clean up old dropdown instances
      this.dropdowns.forEach(dropdown => {
        try {
          dropdown.dispose();
        } catch (error) {
          console.error("Error disposing dropdown:", error);
        }
      });
      this.dropdowns = [];
      
      // Initialize all dropdowns
      const dropdownElementList = document.querySelectorAll('.dropdown-toggle');
      console.log("Found dropdown elements:", dropdownElementList.length);
      
      dropdownElementList.forEach(el => {
        try {
          const dropdown = new Dropdown(el, {
            autoClose: true
          });
          this.dropdowns.push(dropdown);
        } catch (error) {
          console.error("Error creating dropdown:", error);
        }
      });
    },
    
    initNavbarCollapse() {
      console.log("Initializing navbar collapse...");
      
      // Clean up previous navbar collapse instance
      if (this.navbarCollapse) {
        console.log("Disposing previous navbar collapse instance");
        this.navbarCollapse.dispose();
      }
      
      // Initialize navbar collapse
      const navbarCollapse = document.querySelector('#navbarNav');
      console.log("Found navbar collapse element:", !!navbarCollapse);
      
      if (navbarCollapse) {
        try {
          console.log("Creating new Bootstrap collapse instance");
          this.navbarCollapse = new Collapse(navbarCollapse, {
            toggle: false
          });
          console.log("Bootstrap collapse instance created successfully");
          
          // Add event listeners to close menu when clicking links on mobile
          const navLinks = document.querySelectorAll('.navbar-nav .nav-link');
          console.log("Found nav links:", navLinks.length);
          
          navLinks.forEach(link => {
            link.addEventListener('click', () => {
              // Check if we're on mobile by seeing if the toggler is visible
              const toggler = document.querySelector('.navbar-toggler');
              if (toggler && window.getComputedStyle(toggler).display !== 'none') {
                console.log("Hiding navbar on mobile link click");
                this.navbarCollapse.hide();
              }
            });
          });
        } catch (error) {
          console.error("Error initializing Bootstrap collapse:", error);
        }
      } else {
        console.warn("Could not find navbar collapse element with ID #navbarNav");
      }
    },
    
    handleScroll() {
      // Get current scroll position
      const currentScrollY = window.scrollY;
      
      // Get current page path to handle special pages differently
      const currentPath = window.location.pathname;
      
      // Determine if user has scrolled past threshold (e.g., 50px)
      this.isScrolled = currentScrollY > 50;
      
      // Determine scroll direction
      this.scrollDirection = currentScrollY > this.lastScrollY ? 'down' : 'up';
      
      // Check if we're on the dashboard, admin, profile, or courses page 
      const isDashboard = currentPath.includes('/dashboard') || 
                          currentPath.includes('/admin') || 
                          currentPath.includes('/profile') ||
                          currentPath.includes('/courses/');
      
      // Log for troubleshooting
      const prevVisible = this.navbarVisible;
      
      // Special handling for dashboard pages - always show navbar
      if (isDashboard) {
        this.navbarVisible = true;
      } else {
        // Regular pages: Show navbar when scrolling up or at the top, hide when scrolling down
        if (currentScrollY <= 50) {
          // Always show at top of page
          this.navbarVisible = true;
        } else if (this.scrollDirection === 'up') {
          // Show when scrolling up
          this.navbarVisible = true;
        } else if (this.scrollDirection === 'down' && (currentScrollY - this.lastScrollY) > 10) {
          // Hide when scrolling down significantly
          this.navbarVisible = false;
        }
      }
      
      // Log if visibility changed
      if (prevVisible !== this.navbarVisible) {
        console.log("Navbar visibility changed:", {
          path: currentPath,
          isDashboard,
          scrollY: currentScrollY,
          direction: this.scrollDirection,
          visible: this.navbarVisible
        });
      }
      
      // Update last scroll position
      this.lastScrollY = currentScrollY;
    },
    
    // Fetch notification count from API
    async fetchNotificationCount() {
      if (!this.isLoggedIn) return;
      
      try {
        const response = await api.getUnreadCounts();
        this.notificationCount = response.data.unread_notifications || 0;
      } catch (error) {
        console.error('Error fetching notification count:', error);
      }
    },
    
    async handleLogout() {
      console.log('Logging out user...');
      
      try {
        // Try to call the logout API - but don't block on failure
        if (this.$store) {
          await this.$store.dispatch('logout').catch(err => {
            console.warn('Logout API call failed, clearing state locally:', err);
          });
        } else {
          try {
            // If store is not available, try to call API directly
            const api = require('@/services/api').default;
            await api.logout().catch(() => {
              console.warn('API logout failed, proceeding with local logout');
            });
          } catch (e) {
            console.warn('Error importing API service:', e);
          }
        }
      } catch (e) {
        console.warn('Error during logout process:', e);
      } finally {
        // Always clear local storage, regardless of API call success
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        
        // If store exists, explicitly clear auth state
        if (this.$store && this.$store.commit) {
          this.$store.commit('clearAuth');
        }
        
        console.log('User logged out, redirecting to login page');
        
        // Redirect to login page
        this.$router.push('/login');
      }
    }
  },
  beforeUnmount() {
    // Clean up dropdown instances
    this.dropdowns.forEach(dropdown => {
      dropdown.dispose();
    });
    
    // Clean up navbar collapse instance
    if (this.navbarCollapse) {
      this.navbarCollapse.dispose();
    }
    
    // Remove scroll event listener
    window.removeEventListener('scroll', this.handleScroll);
    
    // Clear notification interval
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
    }
  }
}
</script>

<style scoped lang="scss">
.main-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1050; /* Ensure it's higher than bootstrap modals (1040) */
  backdrop-filter: blur(10px);
  background: rgba(0, 0, 0, 0.9);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  will-change: transform;
  transition: transform 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
  transform: translateY(0); /* Ensure navbar starts visible */
  
  // More compact navbar when scrolled
  &.scrolled {
    background: rgba(0, 0, 0, 0.95);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    
    .navbar {
      padding: 10px 0;
    }
  }
  
  // Hide navbar when scrolling down
  &.navbar-hidden {
    transform: translateY(-100%);
  }
}

.navbar {
  padding: 15px 0;
  transition: all 0.3s ease;
  
  .navbar-brand {
    position: relative;
    
    img {
      max-height: 40px;
      transition: all 0.3s ease;
    }
    
    &::after {
      content: '';
      position: absolute;
      height: 5px;
      width: 40%;
      background: linear-gradient(45deg, #00CEC9, #20e3de);
      bottom: -15px;
      left: 0;
      border-radius: 10px;
      opacity: 0;
      transition: all 0.3s ease;
    }
    
    &:hover::after {
      opacity: 1;
      width: 100%;
    }
  }
  
  .navbar-nav {
    gap: 10px;
    
    .nav-item {
      position: relative;
      
      .nav-link {
        color: white;
        font-weight: 500;
        padding: 10px 15px;
        display: flex;
        align-items: center;
        gap: 8px;
        border-radius: 8px;
        transition: all 0.3s ease;
        
        i {
          font-size: 1.2rem;
          transition: transform 0.3s ease;
        }
        
        &:hover {
          background: rgba(255, 255, 255, 0.1);
          
          i {
            transform: translateY(-2px);
            color: var(--primary-color);
          }
        }
        
        &.router-link-active {
          background: rgba(0, 206, 201, 0.15);
          color: var(--primary-color);
          
          i {
            color: var(--primary-color);
          }
          
          &::after {
            content: '';
            position: absolute;
            bottom: 5px;
            left: 50%;
            transform: translateX(-50%);
            width: 5px;
            height: 5px;
            background-color: var(--primary-color);
            border-radius: 50%;
          }
        }
      }
    }
  }
  
  .nav-actions {
    display: flex;
    align-items: center;
    gap: 15px;
    
    .btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 20px;
      border-radius: 8px;
      font-weight: 600;
      transition: all 0.3s ease;
      
      i {
        font-size: 1.1rem;
      }
    }
    
    .btn-nav-login {
      background: transparent;
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.3);
      
      &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: white;
        transform: translateY(-2px);
      }
    }
    
    .btn-nav-register {
      background: linear-gradient(45deg, #00CEC9, #20e3de);
      color: white;
      border: none;
      box-shadow: 0 4px 15px rgba(0, 206, 201, 0.3);
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 206, 201, 0.4);
      }
    }
    
    // Nav Icons (Notifications, Messages)
    .nav-icons {
      display: flex;
      align-items: center;
      gap: 15px;
    }
    
    .nav-icon-wrapper {
      position: relative;
    }
    
    .nav-icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      transition: all 0.3s ease;
      position: relative;
      
      i {
        font-size: 1.2rem;
      }
      
      &:hover {
        background: rgba(255, 255, 255, 0.15);
        transform: translateY(-3px);
      }
    }
    
    .nav-badge {
      position: absolute;
      top: -5px;
      right: -5px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(45deg, #FF416C, #FF4B2B);
      color: white;
      font-size: 0.75rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid #121212;
    }
    
    // User Dropdown Menu
    .user-dropdown {
      .btn {
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: white;
        padding: 8px 15px;
        display: flex;
        align-items: center;
        gap: 10px;
        border-radius: 30px;
        
        &::after {
          margin-left: 8px;
        }
        
        &:hover {
          background: rgba(255, 255, 255, 0.15);
        }
      }
      
      .user-avatar {
        width: 36px;
        height: 36px;
        background: linear-gradient(45deg, #00CEC9, #20e3de);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
      }
      
      .user-avatar-img {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid rgba(255, 255, 255, 0.2);
      }
      
      .user-name {
        max-width: 120px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .dropdown-menu {
        background: rgba(25, 25, 25, 0.95);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0 5px 25px rgba(0, 0, 0, 0.2);
        border-radius: 12px;
        padding: 0;
        margin-top: 10px;
        min-width: 260px;
        
        .dropdown-user-header {
          padding: 15px;
          
          .user-avatar-large {
            width: 48px;
            height: 48px;
            background: linear-gradient(45deg, #00CEC9, #20e3de);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 700;
            font-size: 1.2rem;
            margin-right: 15px;
          }
          
          .user-avatar-large-img {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            object-fit: cover;
            margin-right: 15px;
            border: 2px solid rgba(255, 255, 255, 0.1);
          }
          
          .user-details {
            h6 {
              color: white;
              font-weight: 600;
            }
            
            small {
              color: rgba(255, 255, 255, 0.6);
            }
          }
        }
        
        .dropdown-item {
          color: white;
          padding: 12px 15px;
          border-radius: 0;
          display: flex;
          align-items: center;
          gap: 12px;
          
          i {
            font-size: 1.1rem;
            color: var(--primary-color);
            transition: all 0.3s ease;
          }
          
          &:hover {
            background: rgba(255, 255, 255, 0.08);
            
            i {
              transform: translateX(3px);
            }
          }
          
          &:active {
            background: rgba(0, 206, 201, 0.15);
          }
          
          .badge {
            font-size: 0.7rem;
            padding: 0.35em 0.65em;
          }
        }
        
        .dropdown-divider {
          border-color: rgba(255, 255, 255, 0.05);
          margin: 0;
        }
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 991.98px) {
  .main-header {
    backdrop-filter: none;
    background: #121212;
  }
  
  .navbar {
    .navbar-toggler {
      border-color: rgba(255, 255, 255, 0.2);
      color: white;
      
      &:focus {
        box-shadow: 0 0 0 0.25rem rgba(0, 206, 201, 0.25);
      }
    }
    
    .navbar-nav {
      margin-top: 20px;
      margin-bottom: 20px;
      gap: 5px;
      
      .nav-link {
        padding: 12px 15px;
      }
    }
    
    .nav-actions {
      flex-direction: column;
      width: 100%;
      gap: 15px;
      margin-top: 10px;
      
      .btn {
        width: 100%;
        justify-content: center;
      }
      
      .nav-icons {
        display: flex;
        justify-content: center;
        width: 100%;
        gap: 20px;
      }
      
      .user-dropdown {
        width: 100%;
        
        .btn {
          width: 100%;
          justify-content: center;
          
          .user-name {
            display: inline-block !important;
          }
        }
        
        .dropdown-menu {
          width: 100%;
          max-width: 300px;
          margin-left: auto;
          margin-right: auto;
        }
      }
    }
  }
}
</style>